/**
 * ChatPanel — collapsible AI chat drawer that slides in from the right.
 * Uses the /api/v1/chat/message endpoint to query portfolio data via Claude.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '../Auth/AuthContext'
import { api } from '../../lib/api'
import ChatMessage from './ChatMessage'

const SUGGESTIONS = [
  'Give me a portfolio overview',
  'Show me cash flows by year as a bar chart',
  'What is the TVPI for each fund?',
  'How has FX impacted my EUR funds?',
]

export default function ChatPanel() {
  const { currentOrg } = useAuth()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, loading])

  // Focus input when panel opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200)
    }
  }, [open])

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return

    const userMsg = { role: 'user', content: text.trim() }
    const updatedMessages = [...messages, userMsg]
    setMessages(updatedMessages)
    setInput('')
    setError(null)
    setLoading(true)

    try {
      const response = await api.post('/chat/message', {
        messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
        org_id: currentOrg?.id,
      })

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: response.reply,
          charts: response.charts || [],
        },
      ])
    } catch (err) {
      setError(err.message || 'Failed to get response')
    } finally {
      setLoading(false)
    }
  }, [messages, loading, currentOrg])

  function handleSubmit(e) {
    e.preventDefault()
    sendMessage(input)
  }

  function handleSuggestion(text) {
    sendMessage(text)
  }

  function clearChat() {
    setMessages([])
    setError(null)
  }

  return (
    <>
      {/* Toggle button — fixed bottom-right */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
          open
            ? 'bg-surface-tertiary text-ink border border-edge hover:bg-surface-secondary'
            : 'bg-brand text-ink-onAccent hover:opacity-90'
        }`}
        title={open ? 'Close chat' : 'Ask AI about your portfolio'}
      >
        {open ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      <div
        className={`fixed bottom-0 right-0 z-40 h-full w-full sm:w-[420px] bg-surface border-l border-edge shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-edge-subtle bg-surface-secondary">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-brand/20 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink">Portfolio AI</h3>
              <p className="text-xs text-ink-subtle">Ask questions about your data</p>
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              className="text-xs text-ink-muted hover:text-ink transition px-2 py-1 rounded hover:bg-surface-tertiary"
              title="Clear chat"
            >
              Clear
            </button>
          )}
        </div>

        {/* Messages area */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-ink mb-1">Ask about your portfolio</h4>
              <p className="text-xs text-ink-muted mb-6">I can analyze your funds, cash flows, FX exposure, and more.</p>
              <div className="w-full space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestion(s)}
                    className="w-full text-left px-3 py-2.5 text-xs text-ink-muted bg-surface-secondary hover:bg-surface-tertiary border border-edge-subtle rounded-lg transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <ChatMessage key={i} message={msg} charts={msg.charts} />
              ))}
              {loading && (
                <div className="flex justify-start mb-3">
                  <div className="bg-surface-tertiary rounded-xl rounded-bl-sm px-4 py-3 border border-edge-subtle">
                    <div className="flex items-center gap-2 text-sm text-ink-muted">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      Analyzing your data...
                    </div>
                  </div>
                </div>
              )}
              {error && (
                <div className="mb-3 px-3 py-2 bg-danger/20 border border-danger-border rounded-lg text-xs text-danger-fg">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Input area */}
        <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-edge-subtle bg-surface-secondary">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your portfolio..."
              disabled={loading}
              className="flex-1 px-3 py-2.5 bg-surface border border-edge rounded-lg text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-3 py-2.5 bg-brand text-ink-onAccent rounded-lg hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
